import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Segment, Sidebar } from 'semantic-ui-react';
import ReservationInfo from './reservation';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import './style.less'
import { Actions } from '../../components/shared/constants';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useTextResources, useMessageResources } from '../../language';
import { useLoader } from '../../hooks/useLoader';
import { Hotels, Rooms, RoomTypes, Wings, Calendar as PMSCalendar, RoomPlannedMaintenance, Products } from '../../api/services/next';
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import { MaintenanceStatusEnums, ReservationStatusesEnums, MaintenanceStatusTypes, RoomStatusTypes } from '../../components/shared/statustypes';
import { getContrastYIQ, GetFullName, isWeekendByLocale } from '../../components/shared/utils';
import ValidateModalPermission from '../../components/shared/ValidateModalPermission';
import ShortReservationPopup from '../../components/ShortReservationPopup';
import { PermissionsActions, UserHasPermission } from '../../components/shared/permissionsActions';
import { GetReservationTotals, SaveReservation, UpdateReservationStatus } from '../../api/services/pms';
import _ from 'lodash';
import CalendarToolbar from './toolbar';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
const moment = extendMoment(Moment);

import CreateMenu from './createMenu';
import RestrictionsPopup from '../../components/RestrictionsPopup';
import PriceUpdatePopup from '../../components/PriceUpdatePopup';
import RoomMaintenancePopup from '../../components/RoomMaintenancePopup';
import ResourceDayCell from './resourceDayCell';
import SelectProductPopup from '../../components/SelectProductPopup';
import { useConfirm } from '../../hooks/useConfirm';
import ReservationPolicyUpdateConfirmationPopup from '../../components/ReservationPolicyUpdateConfirmationPopup';
import { toServerDateFormat } from '../../components/shared/utils';
import SchedulerPopup, { MaintenancePopup } from './popup';
import DayUsePopup from './dayUsePopup';

const BUSINESS_DATE_SOURCE_ID = 'businessDate';
const BUSINESS_DATE_EVENT_ID_PREFIX = 'businessdate-';
const DAYUSE_GROUP_EVENT_ID_PREFIX = 'dayuse-group-';

const isBusinessDateEvent = (event) => {
    const sourceId = event?.source?.id;
    const eventId = event?.id;
    return sourceId === BUSINESS_DATE_SOURCE_ID || eventId?.startsWith?.(BUSINESS_DATE_EVENT_ID_PREFIX) === true;
};

// FullCalendar's EventImpl._def can become undefined when mutate() is called on a stale
// EventApi after event sources are rebuilt. The getter `get extendedProps()` then throws
// TypeError: Cannot read properties of undefined (reading 'extendedProps').
// This helper wraps access in a try-catch to prevent blank screen crashes.
const safeExtendedProps = (event) => {
    if (!event) return {};
    try {
        return event.extendedProps || {};
    } catch {
        return {};
    }
};

const Calendar = () => {
    const text = useTextResources()
    const message = useMessageResources()
    const loader = useLoader()
    const confirm = useConfirm();
    const [showInfo, setShowInfo] = useState(false)
    const [showShortReservation, setShowShortReservation] = useState(false)
    const [showRoomMaintenance, setShowRoomMaintenance] = useState(false)
    const [showCancelReservation, setShowCancelReservation] = useState(false)
    const [showCreateMenu, setShowCreateMenu] = useState(false)

    const calendarRef = useRef(null);
    const selectionInfo = useRef(null);
    const newReservationRef = useRef(null);
    const newMaintenenceRef = useRef(null);
    const hoverTimerRef = useRef(null);
    const canEdit = window.optima.user
        ? window.optima.user.ActionIDs.includes(Actions.EditCalendar)
        : true;
    const [viewSettings, setViewSettings] = useLocalStorageState("CalendarSettings", {})

    const [defaultView, setDefaultView] = useState(
        viewSettings.defaultView || 'resourceTimelineSlidingMonth'
    );

    const defaultMonthLabelFormat = useMemo(() => [{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultWeekLabelFormat = useMemo(() => [{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultDuration = useMemo(() => ({ hours: 24, }), []);

    const [reservations, setReservations] = useState([])
    const [reservationTotals, setReservationTotals] = useState({})
    const [maintenance, setMaintenance] = useState([])
    const [yieldData, setYieldData] = useState([])
    const [dateParameters, setDateParameters] = useState([])
    const [availabilityData, setAvailabilityData] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [dateInfo, setDateInfo] = useState(null);
    const [resources, setResources] = useState([]);
    const [hotels, setHotels] = useState()
    const [rooms, setRooms] = useState()
    const [roomtypes, setRoomTypes] = useState()
    const [wings, setWings] = useState()
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    const [selectedTotals, setSelectedTotals] = useState(null);
    const [productSelection, setProductSelection] = useState({ open: false });
    const [policyDetails, setPolicyDetails] = useState(null);
    const [showPolicyChangePopup, setShowPolicyChangePopup] = useState(false);
    
    const [showPopup, setShowPopup] = useState(false);
    const [popupContext, setPopupContext] = useState(null);
    const [popupReservation, setPopupReservation] = useState(null);

    const [showMaintenancePopup, setShowMaintenancePopup] = useState(false);
    const [maintenancePopupContext, setMaintenancePopupContext] = useState(null);
    const [popupMaintenance, setPopupMaintenance] = useState(null);

    const [showMultiplePopup, setShowMultiplePopup] = useState(false);
    const [multiplePopupContext, setMultiplePopupContext] = useState(null);
    const [multiplePopupEvent, setMultiplePopupEvent] = useState(null);

    const selectedEventRef = useRef(null);
    selectedEventRef.current = selectedEvent;

    const isInteractingRef = useRef(false);

    const defaultItemKey = "DefaultCalendarCreateItem";

    const currentViewType = dateInfo?.view?.type || defaultView;
    const useGroupView = currentViewType !== 'resourceTimelineDay';

    const views = useMemo(() => ({
        resourceTimelineDay: {
            slotLabelFormat: [
                { day: '2-digit', weekday: 'long' },
                { hour: '2-digit', hour12: false, minute: '2-digit' },
            ],
            slotDuration: { hours: 1 },
            slotLabelInterval: { hours: 1 },
        },
        resourceTimelineWeek: {
            slotLabelFormat: defaultWeekLabelFormat,
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineBiWeek: {
            slotLabelFormat: defaultWeekLabelFormat,
            type: 'resourceTimeline',
            duration: { days: 14 },
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineMonth: {
            slotLabelFormat: defaultMonthLabelFormat,
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
        resourceTimelineSlidingMonth: {
            slotLabelFormat: defaultMonthLabelFormat,
            type: 'resourceTimeline',
            dayCount: 30,
            duration: { days: 30 },
            slotDuration: defaultDuration,
            slotLabelInterval: defaultDuration,
        },
    }), [defaultDuration, defaultMonthLabelFormat, defaultWeekLabelFormat])

    const hotelsWithRooms = useMemo(() => {
        if (!hotels || !rooms) {
            return null;
        }
        return hotels.filter((hotel) => {
            return rooms.some((room) => room.HotelID === hotel.ID);
        });
    }, [hotels, rooms])

    const maintenanceEventDataTransform = useCallback((m) => ({
        id: m.Id,
        title: m.Description?.replaceAll("\n", " ") || text(MaintenanceStatusEnums[m.MaintenanceStatusTypeId]) || MaintenanceStatusTypes[m.MaintenanceStatusTypeId],
        start: moment(m.StartDate).hour(12).format(),
        end: moment(m.EndDate).hour(12).format(),
        resourceId: m.RoomId,
        classNames: [`maintenance-type-${m.MaintenanceStatusTypeId}`],
        extendedProps: { maintenance: m },
        color: m.Color,
        textColor: getContrastYIQ(m.Color || "3788d8")
    }), [text])

    // Process reservations to handle split rooms (heterogeneous daily accommodations)
    // When a reservation has different rooms on different dates, split it into multiple events
    const processedReservations = useMemo(() => {
        if (!reservations || !hotelsWithRooms) return [];
        
        const result = [];
        
        reservations.forEach(reservation => {
            const dailyAccommodations = reservation.ReservationDailyAccommodations;
            
            // Check if all daily accommodations have the same room
            const isHomogeneous = !dailyAccommodations?.length || 
                _.every(dailyAccommodations, ['RoomId', reservation.RoomId]);
            
            if (isHomogeneous) {
                // Single event - all nights in same room
                result.push(reservation);
            } else {
                // Split into multiple events by grouping consecutive dates with same room
                const grouped = _.reduce(dailyAccommodations, (acc, value, index, collection) => {
                    if (!index || value.RoomId !== collection[index - 1].RoomId) {
                        acc.push([]);
                    }
                    acc[acc.length - 1].push(value);
                    return acc;
                }, []);
                
                grouped.forEach((group, idx) => {
                    const minDate = _.minBy(group, 'Date');
                    const maxDate = _.maxBy(group, 'Date');
                    const roomId = group[0].RoomId;
                    const checkin = moment(minDate.Date);
                    const checkout = moment(maxDate.Date).add(1, 'day');
                    
                    if (roomId) {
                        // Create a pseudo-reservation for this segment
                        result.push({
                            ...reservation,
                            _splitId: `${reservation.Id}-${idx}`, // Unique ID for this segment
                            _isHeteroSegment: true,
                            _originalId: reservation.Id,
                            RoomId: roomId,
                            CheckInDate: checkin.isSame(reservation.CheckInDate, 'day') 
                                ? reservation.CheckInDate 
                                : checkin.hour(12).toISOString(),
                            CheckOutDate: checkout.isSame(reservation.CheckOutDate, 'day') 
                                ? reservation.CheckOutDate 
                                : checkout.hour(12).toISOString(),
                        });
                    }
                });
            }
        });

        // Group day-use reservations (same check-in and check-out day), for all views except daily
        if (useGroupView) {
            // Find day-use reservations: check-in and check-out on the same calendar day
            const dayUseReservations = result.filter(r => {
                if (r._isGroupingEvent) return false;
                const checkIn = moment(r.CheckInDate);
                const checkOut = moment(r.CheckOutDate);
                return checkIn.isSame(checkOut, 'day');
            });

            if (dayUseReservations.length > 0) {
                // Group by roomId + date
                const grouped = _.groupBy(dayUseReservations, r =>
                    `${r.RoomId}:${moment(r.CheckInDate).format('YYYY-MM-DD')}`
                );

                // Track which reservations are grouped (to remove them from result)
                const groupedIds = new Set();

                // Pre-index reservations by RoomId with precomputed moment objects
                const reservationsByRoomId = new Map();

                result.forEach(r => {
                    // Skip grouping events, as in the original filter logic
                    if (r._isGroupingEvent) return;

                    const roomKey = r.RoomId;
                    const checkIn = moment(r.CheckInDate);
                    const checkOut = moment(r.CheckOutDate);

                    let list = reservationsByRoomId.get(roomKey);
                    if (!list) {
                        list = [];
                        reservationsByRoomId.set(roomKey, list);
                    }

                    list.push({
                        reservation: r,
                        checkIn,
                        checkOut
                    });
                });

                _.forEach(grouped, (groupReservations, key) => {
                    if (groupReservations.length > 1) {
                        // Mark all individual reservations in this group for removal
                        groupReservations.forEach(r => {
                            groupedIds.add(r._splitId || r.Id);
                        });

                        const [roomId, dateStr] = key.split(':');
                        const targetDate = moment(dateStr);

                        // Find other (non-day-use) reservations on the same room+date for smart positioning
                        const roomReservations = reservationsByRoomId.get(Number(roomId)) || [];
                        const otherReservations = roomReservations.filter(item => {
                            const { reservation, checkIn, checkOut } = item;

                            if (groupedIds.has(reservation._splitId || reservation.Id)) return false;

                            // Check if this reservation overlaps with the target date
                            return checkIn.isSameOrBefore(targetDate, 'day') &&
                                   checkOut.isSameOrAfter(targetDate, 'day');
                        });

                        // Determine badge position based on other reservations
                        // Badge width in hours depends on view type for optimal visibility
                        const badgeWidthHours = currentViewType === 'resourceTimelineWeek' ? 3
                            : currentViewType === 'resourceTimelineBiWeek' ? 6 : 12;
                        let startHour;

                        if (otherReservations.length === 0) {
                            startHour = (24 / 2) - (badgeWidthHours / 2);
                        } else {
                            // Check for reservations ending on this day
                            // Use precomputed moment objects from the index
                            const endingReservations = [];
                            const beginningReservations = [];

                            otherReservations.forEach(item => {
                                const { reservation, checkIn, checkOut } = item;

                                if (checkIn.isBefore(targetDate, 'day') && checkOut.isSame(targetDate, 'day')) {
                                    endingReservations.push({ reservation, checkIn, checkOut });
                                } else if (checkIn.isSame(targetDate, 'day') && checkOut.isAfter(targetDate, 'day')) {
                                    beginningReservations.push({ reservation, checkIn, checkOut });
                                }
                            });

                            if (endingReservations.length > 0) {
                                const maxCheckoutHour = Math.max(...endingReservations.map(item => item.checkOut.hour()));
                                const availableSpace = 24 - maxCheckoutHour;
                                startHour = maxCheckoutHour + (availableSpace / 2) - (badgeWidthHours / 2);
                            } else if (beginningReservations.length > 0) {
                                const minCheckinHour = Math.min(...beginningReservations.map(item => item.checkIn.hour()));
                                startHour = (minCheckinHour / 2) - (badgeWidthHours / 2);
                            } else {
                                // Default center
                                startHour = (24 / 2) - (badgeWidthHours / 2);
                            }
                        }
                        
                        // Calculate end hour based on badge width
                        const endHour = startHour + badgeWidthHours;
                        
                        // Convert decimal hours to hour:minute format
                        const checkInDate = targetDate.clone()
                            .hour(Math.floor(startHour))
                            .minute((startHour % 1) * 60)
                            .toISOString();
                        const checkOutDate = targetDate.clone()
                            .hour(Math.floor(endHour))
                            .minute((endHour % 1) * 60)
                            .toISOString();

                        // Create a grouping event representing all day-use reservations for this room+date
                        result.push({
                            _isGroupingEvent: true,
                            _groupKey: key,
                            Id: `${DAYUSE_GROUP_EVENT_ID_PREFIX}${key}`,
                            RoomId: Number(roomId),
                            HotelId: groupReservations[0].HotelId,
                            CheckInDate: checkInDate,
                            CheckOutDate: checkOutDate,
                            _groupedReservations: groupReservations,
                            _groupCount: groupReservations.length,
                        });
                    }
                });

                // Remove individual day-use reservations that are now grouped
                if (groupedIds.size > 0) {
                    return result.filter(r => {
                        const id = r._splitId || r.Id;
                        return !groupedIds.has(id);
                    });
                }
            }
        }
        
        return result;
    }, [reservations, hotelsWithRooms, useGroupView, currentViewType]);

    const reservationEventDataTransform = useCallback((r) => {
        // Handle grouping events (multiple day-use reservations)
        if (r._isGroupingEvent) {
            const count = r._groupCount;
            return {
                id: r.Id,
                title: String(count),
                start: r.CheckInDate,
                end: r.CheckOutDate,
                resourceId: r.RoomId,
                classNames: ['grouping-event'],
                extendedProps: {
                    isGroupingEvent: true,
                    events: r._groupedReservations,
                    groupCount: count,
                },
                editable: false,
                durationEditable: false,
                startEditable: false,
                resourceEditable: false,
            };
        }

        const title = viewSettings.showReservationNumber ? `${r.Number} ${GetFullName(r.Guest)}` : GetFullName(r.Guest);
        const hotel = hotelsWithRooms.find(h => h.Id == r.HotelId)
        // Use original reservation ID for totals lookup
        const originalId = r._originalId || r.Id;
        const totals = reservationTotals[originalId];
        let classname = "";
        switch (viewSettings.reservationIcon) {
            case "24h":
                if (r.CheckInDate && moment(r.CheckInDate).isAfter(moment().subtract(24, 'hours'))) {
                    classname = "state-24h"
                }
                break;
            case "balance":
                if (totals) {
                    const balance = totals.TotalAmountDebitAfterTax + totals.TotalAmountCreditAfterTax;
                    const notInvoiced = totals.TotalAmountDebitBeforeTax - totals.TotalAmountDebitBeforeTaxHasInvoice;

                    if (Math.abs(notInvoiced) < 0.01) {
                        classname = "state-balance-invoiced";
                    } else if (Math.abs(balance) < 0.01) {
                        classname = "state-balance-nothing";
                    } else if (Math.abs(totals.TotalAmountCreditAfterTax) > 0.01) {
                        classname = "state-balance-partial";
                    } else {
                        classname = "state-balance-full";
                    }
                }
                break;
            case "agent":
                if (r.Agent) {
                    const agentName = (r.Agent.Name || 'unknown').toLowerCase().replace(/\s+/g, '-');
                    classname = `state-agent state-agent-${agentName}`;
                }
                break;
            case "company":
                if (r.Company) {
                    const companyName = (r.Company.Name || 'unknown').toLowerCase().replace(/\s+/g, '-');
                    classname = `state-company state-company-${companyName}`;
                }
                break;
            case "confirmation":
                if (r.ConfirmationNumber) {
                    classname = "state-confirmation"
                }
                break;
            case "documents":
                const reservationDocuments = [];
                    if (r.Images) {
                      reservationDocuments.push(...r.Images);
                    }
                    if (r.Guest?.Images) {
                      reservationDocuments.push(...r.Guest.Images);
                    }
                if (reservationDocuments.length > 0) {
                    classname = "state-documents"
                }
                break;
            default:
                break;
        }

        // Get the initial letters for agent and company separately
        const agentInitial = r.Agent?.Name?.charAt(0) || '';
        const companyInitial = r.Company?.Name?.charAt(0) || '';

        // Use split ID if this is a hetero segment, otherwise use normal ID
        const eventId = r._splitId || r.Id;
        
        // Add 'hetero' class for split segments to style them differently and disable editing
        const heteroClass = r._isHeteroSegment ? 'hetero' : '';

        return {
            id: eventId,
            title: title,
            start: r.CheckInDate,
            end: r.CheckOutDate,
            classNames: [classname, heteroClass].filter(Boolean),
            resourceId: r.RoomId,
            extendedProps: { 
                reservation: r, 
                hotel, 
                totals, 
                agentInitial, 
                companyInitial,
                isHeteroSegment: r._isHeteroSegment,
                originalReservationId: r._originalId
            },
            color: r.Color,
            textColor: getContrastYIQ(r.Color || "3788d8"),
            // Disable drag/resize for hetero segments (split room reservations)
            editable: !r._isHeteroSegment,
            durationEditable: !r._isHeteroSegment,
            startEditable: !r._isHeteroSegment,
            resourceEditable: !r._isHeteroSegment
        }
    }, [hotelsWithRooms, viewSettings.reservationIcon, viewSettings.showReservationNumber, reservationTotals])

    // Compute virtual events for complex rooms - bi-directional blocking
    // 1. When a component room has maintenance, show "Occupied" on the complex parent room
    // 2. When a complex room has a reservation, show "Occupied" on the component rooms
    // 3. When a component room has a reservation, show "Occupied" on the complex parent room
    const virtualEvents = useMemo(() => {
        if (!rooms) return [];
        
        // Find complex rooms (rooms with RoomComplexPartComplexRooms)
        const complexRooms = rooms.filter(r => r.IsVirtual && r.RoomComplexPartComplexRooms?.length > 0);
        
        if (complexRooms.length === 0) return [];
        
        const virtualEvts = [];
        
        // Flow 1: Component room maintenance → Block complex parent room
        maintenance.forEach(m => {
            // Find all complex rooms that contain this maintenance's room as a part
            const complexParents = complexRooms.filter(complex => 
                complex.RoomComplexPartComplexRooms.some(part => part.PartRoomId === m.RoomId)
            );
            
            complexParents.forEach(complex => {
                virtualEvts.push({
                    Id: `virtual-maint-${m.Id}-${complex.Id}`,
                    RoomId: complex.Id,
                    HotelId: m.HotelId,
                    StartDate: m.StartDate,
                    EndDate: m.EndDate,
                    Description: text("MaintenanceBlockedDueToComplex"),
                    MaintenanceStatusTypeId: m.MaintenanceStatusTypeId,
                    Color: m.Color || "#b7b7b7",
                    IsVirtual: true,
                    SourceMaintenanceId: m.Id
                });
            });
        });
        
        // Flow 2: Complex room reservation → Block component rooms
        // Only for main Vila Complex (rooms that are NOT parts of another complex)
        reservations.forEach(r => {
            // Check if this reservation is on a complex room
            const complexRoom = complexRooms.find(c => c.Id === r.RoomId);
            
            // Only block components if this complex room is NOT itself a part of another complex
            // (i.e., it's the main Vila Complex, not a sub-complex like "חדר קומפלקס 1")
            const isMainComplex = complexRoom && 
                complexRoom.RoomComplexPartComplexRooms?.length > 0 && 
                (!complexRoom.RoomComplexPartPartRooms || complexRoom.RoomComplexPartPartRooms.length === 0);
            
            if (isMainComplex) {
                // Create blocking events for each component room
                complexRoom.RoomComplexPartComplexRooms.forEach(part => {
                    virtualEvts.push({
                        Id: `virtual-res-${r.Id}-${part.PartRoomId}`,
                        RoomId: part.PartRoomId,
                        HotelId: r.HotelId,
                        StartDate: r.CheckInDate,
                        EndDate: r.CheckOutDate,
                        Description: text("MaintenanceBlockedDueToComplex"),
                        MaintenanceStatusTypeId: 2, // Use OOO style for blocking
                        Color: "#b7b7b7",
                        IsVirtual: true,
                        SourceReservationId: r.Id
                    });
                });
            }
        });

        // Flow 3: Component room reservation → Block complex parent room
        // When a sub-room has a reservation, the main complex should be blocked
        reservations.forEach(r => {
            // Find the room that has this reservation
            const reservedRoom = rooms.find(room => room.Id === r.RoomId);

            // Check if this room is a part of a complex (has parent complex rooms)
            if (reservedRoom?.RoomComplexPartPartRooms?.length > 0) {
                // For each parent complex room, create a blocking virtual event
                reservedRoom.RoomComplexPartPartRooms.forEach(partRelation => {
                    virtualEvts.push({
                        Id: `virtual-res-parent-${r.Id}-${partRelation.ComplexRoomId}`,
                        RoomId: partRelation.ComplexRoomId,
                        HotelId: r.HotelId,
                        StartDate: r.CheckInDate,
                        EndDate: r.CheckOutDate,
                        Description: text("MaintenanceBlockedDueToComplex"),
                        MaintenanceStatusTypeId: 2, // Use OOO style for blocking
                        Color: "#b7b7b7",
                        IsVirtual: true,
                        SourceReservationId: r.Id
                    });
                });
            }
        });

        return virtualEvts;
    }, [rooms, maintenance, reservations, text]);

    const eventSources = useMemo(() => [
        {
            id: 'reservations',
            className: 'reservation-event',
            eventDataTransform: reservationEventDataTransform,
            events: processedReservations

        },
        {
            id: 'maintenance',
            className: 'maintenance-event',
            eventDataTransform: maintenanceEventDataTransform,
            events: maintenance
        },
        {
            id: 'virtualMaintenance',
            className: 'maintenance-event virtual-event',
            eventDataTransform: maintenanceEventDataTransform,
            events: virtualEvents
        },
        {
            id: 'businessDate',
            className: 'business-date-event',
            display: 'background',
            events: resources.map(resource => {
                const businessDate = moment(resource.extendedProps.hotel?.HotelParameter?.CurrentBusinessDate)
                return {
                    id: `businessdate-${resource.id}`,
                    resourceId: resource.id,
                    start: businessDate.startOf('day').toISOString(),
                    end: businessDate.endOf('day').toISOString(),
                    interactive: false,
                }
            })
        }
    ], [reservationEventDataTransform, maintenanceEventDataTransform, processedReservations, maintenance, virtualEvents, resources])

    useEffect(() => {
        (async () => {

            try {
                loader(true)
                const [hotels, rooms, roomtypes, wings] = await Promise.all([
                    Hotels.GetHotels({
                        IncludeOnlyHotelsWithUserPermissions: true,
                        includes: ['HotelFeatures', 'HotelParameter', 'Location']
                    }),
                    Rooms.GetRooms({
                        includes: ['RoomHousekeepingStatus', 'RoomComplexPartComplexRooms', 'RoomComplexPartPartRooms']
                    }),
                    RoomTypes.GetRoomTypes(),
                    Wings.GetWings()
                ]);
                setHotels(hotels)
                setRoomTypes(roomtypes)
                setWings(wings)
                setRooms(rooms)
            } catch (error) {
                onError(error)
            }
            finally {
                loader(false)
            }
        })()
    }, [loader])

    // Build resources based on rooms and selected hotels filter
    useEffect(() => {
        if (!hotels || !rooms || !roomtypes || !wings) return;

        const filteredResources = rooms
            .filter(room => selectedHotelIds.length === 0 || selectedHotelIds.includes(room.HotelId))
            .map(room => {
                const hotel = hotels.find(hotel => hotel.Id === room.HotelId)
                const roomtype = roomtypes.find(type => type.Id === room.TypeId)
                const wing = wings.find(wing => wing.Id === room.WingId)
                // Skip if hotel, roomtype, or wing is null
                if (!hotel || !roomtype || !wing) {
                    return null;
                }

                return {
                    id: room.Id,
                    title: `${room.Number}   ${roomtype.Name}`,
                    group: `${hotel.Name} - ${wing.Name}`,
                    extendedProps: { room, hotel, wing },
                    order: room.DisplayOrder
                }
            })
            .filter(Boolean);
        setResources(filteredResources);
    }, [hotels, rooms, roomtypes, wings, selectedHotelIds])

    useEffect(() => {
        if (dateInfo && hotelsWithRooms) {
            (async () => {
                try {
                    loader(true);
                    const [reservations, maintenance] = await Promise.all([PMSCalendar.GetReservations({
                        HotelIds: hotelsWithRooms.map(x => x.Id),
                        FromCheckOutDate: dateInfo.startStr,
                        ToCheckInDate: dateInfo.endStr,
                        AssignedRoomsOnly: true,
                        includes: ["RoomType"]
                    }),
                    RoomPlannedMaintenance.GetRoomPlannedMaintenances({
                        HotelIds: hotelsWithRooms.map(x => x.Id),
                        FromDate: dateInfo.startStr,
                        ToDate: dateInfo.endStr,
                        Includes: ["Room","TapechartDisplaySettings"]
                    })]);

                    setReservations(reservations);
                    setMaintenance(maintenance);
                } catch (error) {
                    onError(error);
                } finally {
                    loader(false);
                }
            })();
        }
    }, [dateInfo, hotelsWithRooms, loader]);

    const refetchReservations = useCallback(async () => {
        if (dateInfo && hotelsWithRooms) {
            try {
                loader(true);
                const reservations = await PMSCalendar.GetReservations({
                    HotelIds: hotelsWithRooms.map(x => x.Id),
                    FromCheckOutDate: dateInfo.startStr,
                    ToCheckInDate: dateInfo.endStr,
                    AssignedRoomsOnly: true,
                    includes: ["RoomType"]
                });
                setReservations(reservations);
            } catch (error) {
                onError(error);
            } finally {
                loader(false);
            }
        }
    }, [dateInfo, hotelsWithRooms, loader]);

    // Fetch reservation totals asynchronously when reservations change
    useEffect(() => {
        if (reservations.length > 0 && hotelsWithRooms) {
            (async () => {
                try {
                    const totals = _.flatMap(
                        await Promise.all(
                            _.map(_.groupBy(reservations, 'HotelId'), (hotelReservations, HotelId) => {
                                const hotel = hotelsWithRooms.find(x => x.Id == HotelId);
                                return GetReservationTotals({
                                    ReservationIDs: _.map(hotelReservations, 'Id'),
                                    HotelId: HotelId,
                                    TargetCurrencyID: hotel?.MainCurrencyId
                                });
                            })
                        )
                    );

                    // Convert totals array to a lookup object keyed by ReservationID
                    const totalsMap = {};
                    totals.forEach(t => {
                        totalsMap[t.ReservationID] = t;
                    });
                    setReservationTotals(totalsMap);
                } catch (error) {
                    onWarning(error);
                }
            })();
        } else {
            setReservationTotals({});
        }
    }, [reservations, hotelsWithRooms]);

    const getNewProductID = useCallback((reservation) => {
        const spec = {
            HotelID: reservation.HotelId,
            IncludeDayPricing: true,
            IsDayUse: moment(reservation.CheckInDate).isSame(
                reservation.CheckOutDate,
                'day'
            ),
            MaxAdults: reservation.Adults,
            MaxChildren: reservation.Children,
            MaxInfants: reservation.Infants,
            RoomTypeId: reservation.RoomTypeId,
            StartDate: reservation.CheckInDate,
            EndDate: reservation.CheckOutDate,
            AvoidAllRestriction: true,
            IsNewAlgo: true,
        };

        return new Promise((resolve) => {
            setProductSelection({
                open: true,
                spec: spec,
                cancel: () => {
                    setProductSelection({
                        open: false,
                    });
                    resolve(null);
                },
                select: (productId) => {
                    setProductSelection({
                        open: false,
                    });
                    resolve(productId);
                },
            });
        });
    }, []);

    const onEventClick = useCallback((info) => {
        if (isBusinessDateEvent(info?.event)) {
            return;
        }

        // Handle grouped day-use events (multiple reservations on same room+day in month view)
        if (safeExtendedProps(info.event).isGroupingEvent) {
            setMultiplePopupEvent(info.event);
            setMultiplePopupContext(info.el);
            setShowMultiplePopup(true);
            return;
        }

        // Close multiple popup when clicking other events
        setShowMultiplePopup(false);
        setMultiplePopupContext(null);
        setMultiplePopupEvent(null);

        setSelectedEvent(info.event)
        if (safeExtendedProps(info?.event).maintenance) {
            setShowRoomMaintenance(true);
        }
        else {
            setShowInfo(true);
        }
    }, []);

    const onEventEnter = useCallback((info) => {
        if (isBusinessDateEvent(info?.event)) {
            return;
        }
      
        if (isInteractingRef.current) {
            return;
        }

        // Clear any existing timer
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }

        const ext = safeExtendedProps(info?.event);

        // Skip hover popup for grouping events (handled by click)
        if (ext.isGroupingEvent) {
            return;
        }
        // Close multiple popup when hovering other events
        setShowMultiplePopup(false);
        setMultiplePopupContext(null);
        setMultiplePopupEvent(null);

        // Check if it's a reservation event
        if (ext.reservation) {
            // Set a new timer to show popup after 500ms
            hoverTimerRef.current = setTimeout(() => {
                setPopupReservation(ext.reservation);
                setPopupContext(info.el);
                setShowPopup(true);
            }, 500);
            return;
        }

        // Check if it's a maintenance event (Out of Service / Out of Order)
        if (ext.maintenance) {
            hoverTimerRef.current = setTimeout(() => {
                const maintenanceObj = Array.isArray(ext.maintenance) && ext.maintenance.length
                    ? ext.maintenance[0]
                    : ext.maintenance;
                setPopupMaintenance(maintenanceObj);
                setMaintenancePopupContext(info.el);
                setShowMaintenancePopup(true);
            }, 500);
            return;
        }
    }, []);

    const onEventLeave = useCallback(() => {
        // Clear the timer if mouse leaves before 500ms
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setShowPopup(false);
        setShowMaintenancePopup(false);
    }, []);

    const hideHoverPopup = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
        setShowPopup(false);
        setShowMaintenancePopup(false);
    }, []);

    const onEventDragStart = useCallback(() => {
        isInteractingRef.current = true;
        hideHoverPopup();
    }, [hideHoverPopup]);

    const onEventDragStop = useCallback(() => {
        isInteractingRef.current = false;
    }, []);

    const onEventResizeStart = useCallback(() => {
        isInteractingRef.current = true;
        hideHoverPopup();
    }, [hideHoverPopup]);

    const onEventResizeStop = useCallback(() => {
        isInteractingRef.current = false;
    }, []);

    const handleMultiplePopupReservationClick = useCallback((reservation) => {
        if (!reservation?.Id) {
            return;
        }
        
        const calendar = calendarRef.current?.getApi();
        if (calendar) {
            let fcEvent = calendar.getEventById(String(reservation.Id));
            
            if (!fcEvent && reservation._splitId) {
                fcEvent = calendar.getEventById(String(reservation._splitId));
            }
            
            if (fcEvent) {
                setSelectedEvent(fcEvent);
                setShowMultiplePopup(false);
                setMultiplePopupContext(null);
                setMultiplePopupEvent(null);
                setShowInfo(true);
                return;
            }
        }
        
        // Fallback for grouped day-use reservations not in calendar:
        // Create a plain object with only extendedProps (no EventApi methods)
        // All event method calls are guarded with optional chaining
        const hotel = hotelsWithRooms?.find(h => h.Id === reservation.HotelId);
        const totals = reservationTotals?.[reservation.Id];
        
        setSelectedEvent({
            extendedProps: { reservation, hotel, totals },
            id: String(reservation.Id),
        });
        setShowMultiplePopup(false);
        setMultiplePopupContext(null);
        setMultiplePopupEvent(null);
        setShowInfo(true);
    }, [hotelsWithRooms, reservationTotals]);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
                hoverTimerRef.current = null;
            }
        };
    }, []);
    // Check if a reservation's new position conflicts with existing maintenance records
    // Uses date-only (startOf('day')) comparison to avoid time component issues.
    // In hotel systems, CheckOutDate is the day the guest leaves and maintenance StartDate
    // is the day maintenance begins - same day is NOT a conflict.
    const hasMaintenanceConflict = useCallback((roomId, checkInDate, checkOutDate) => {
        if (!maintenance || !maintenance.length) return false;
        const resStart = moment(checkInDate).startOf('day');
        const resEnd = moment(checkOutDate).startOf('day');
        return maintenance.some(m =>
            m.RoomId === roomId &&
            moment(m.StartDate).startOf('day').isBefore(resEnd) &&
            moment(m.EndDate).startOf('day').isAfter(resStart)
        );
    }, [maintenance]);

    const onEventDrop = useCallback(async (info) => {
        hideHoverPopup();

        const confirmMessages = {
            upgradeRoom: {
                header: text('UpgradeRoomType'),
                body: text('WouldYouLikeToUpgradeThisReservation'),
            },
            changeDates: {
                header: text('ChangeReservationDates'),
                body: text('WouldYouLikeToChangeThisReservationDates'),
            },
        };

        const ext = safeExtendedProps(info.event);
        if (ext.reservation) {
            let reservation = structuredClone(ext.reservation);

            if (reservation.IsDoNotChangeRoom) {
                if (!UserHasPermission(PermissionsActions.ToggleRoomLock)) {
                    onWarning(text("NoPermission"));
                    info.revert();
                    return;
                }
                if (!(await confirm({
                    header: text('UnlockRoom'),
                    body: text('ConfirmUnlockRoom'),
                }))) {
                    info.revert();
                    return;
                }
                reservation.IsDoNotChangeRoom = false;
            }

            if (info.delta.days !== 0) {
                const getAdjustedDate = (date) =>
                    moment(date).add(info.delta.days, 'days').toDate();
                // Dates were changed, update daily accommodations and addons with new dates
                if (!(await confirm(confirmMessages.changeDates))) {
                    info.revert();
                    return;
                }

                if (moment(info.event.start).isBefore(moment())) {
                    onWarning('Cannot move reservation to past date!');
                    info.revert();
                    return;
                }
                reservation.CheckInDate = info.event.start;
                reservation.CheckOutDate = info.event.end;
                reservation.ReservationDailyAccommodations.forEach(
                    (accommodation) => {
                        accommodation.Date = getAdjustedDate(accommodation.Date)
                    }
                );
                reservation.ReservationDailyAddons.forEach(
                    (addon) => {
                        addon.Date = getAdjustedDate(addon.Date);
                        addon.ConsumptionDate = getAdjustedDate(addon.ConsumptionDate);
                    });
            }
            if (info.delta.hours !== 0 || info.delta.minutes !== 0 || info.delta.seconds !== 0) {
                reservation.Eta = moment(info.event.start).format('HH:mm:ss');
                reservation.Edt = moment(info.event.end).format('HH:mm:ss');
            }

            if (info.newResource) {
                // Room and possibly roomtype was changed, update reservation and daily items with new data
                if (
                    info.oldResource.extendedProps.hotelId !==
                    info.newResource.extendedProps.hotelId
                ) {
                    onWarning('Cannot move reservation to a different hotel!');
                    info.revert();
                    return;
                }

                const oldRoom = info.oldResource.extendedProps.room;
                const newRoom = info.newResource.extendedProps.room;
                if (oldRoom.TypeId === newRoom.TypeId) {
                    // Same roomtype, no upgrade, so just update the correct room id
                    reservation.RoomId = newRoom.Id;
                    reservation.ReservationDailyAccommodations.forEach(
                        (accommodation) => {
                            accommodation.RoomId = newRoom.Id;
                        }
                    );
                } else {
                    if (await confirm(confirmMessages.upgradeRoom)) {
                        reservation.UpgradedFromRoomTypeId = reservation.RoomTypeId;
                        reservation.RoomTypeId = newRoom.TypeId;
                        reservation.RoomId = newRoom.Id;
                        reservation.ReservationDailyAccommodations.forEach(
                            (accommodation) => {
                                accommodation.UpgradedFromRoomTypeId = accommodation.RoomTypeId;
                                accommodation.RoomTypeId = newRoom.TypeId;
                                accommodation.RoomId = newRoom.Id;
                            }
                        );
                    } else {
                        const isPostBooking =
                            reservation.ReservationDailyAccommodations.find(
                                (x) => x.FolioTransactionID > 0
                            );
                        if (isPostBooking) {
                            info.revert();
                            return;
                        }

                        reservation.RoomTypeId = newRoom.TypeId;
                        reservation.RoomId = newRoom.Id;
                        // Show product select dialog
                        const newProductId = await getNewProductID(reservation);
                        if (newProductId) {
                            reservation.ProductId = newProductId;
                            // Remove any daily values to rebuild by the server when changing product
                            reservation.ReservationDailyAccommodations = [];
                            reservation.ReservationDailyAddons = [];
                        } else {
                            info.revert();
                            return;
                        }
                    }
                }
            }

            // Check for maintenance conflict before saving
            if (hasMaintenanceConflict(reservation.RoomId, reservation.CheckInDate, reservation.CheckOutDate)) {
                onWarning(message('MaintenanceConflictMessage'));
                info.revert();
                return;
            }

            try {
                const newPolicyResult = await checkForPolicyChange(reservation);
                if (newPolicyResult) {
                    reservation.ReservationPolicyId = newPolicyResult.Id;
                    reservation.ReservationPolicy = newPolicyResult;
                }

                loader(true)

                await SaveReservation(reservation);
                if (info.event?.setExtendedProp) {
                    info.event.setExtendedProp('reservation', reservation);
                }

                // Update reservations state to trigger virtualEvents recalculation
                // This ensures villa/complex room blocking is shown immediately
                setReservations(prevReservations =>
                    prevReservations.map(r => r.Id === reservation.Id ? reservation : r)
                );

                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else if (ext.maintenance) {
            // Confirm the change with the user (same pattern as reservation drag)
            if (!(await confirm({
                header: text('ChangeMaintenanceDates'),
                body: text('WouldYouLikeToChangeThisMaintenanceDates'),
            }))) {
                info.revert();
                return;
            }

            const maintenanceObj = Array.isArray(ext.maintenance) && ext.maintenance.length ?
                ext.maintenance[0] : ext.maintenance;
            const maintenanceData = {
                ...maintenanceObj,
                StartDate: toServerDateFormat(info.event.start),
                EndDate: toServerDateFormat(info.event.end),
            };
            if (info.newResource) {
                if (
                    info.oldResource.extendedProps.hotelId !==
                    info.newResource.extendedProps.hotelId
                ) {
                    onWarning('Cannot move maintenance to a different hotel!');
                    info.revert();
                    return;
                }
                const newRoom = info.newResource.extendedProps.room;
                maintenanceData.RoomId = newRoom.Id;
                maintenanceData.RoomTypeId = newRoom.TypeId;
                maintenanceData.Room = newRoom;
            } else {
                maintenanceData.Room = info.event.getResources().at(0).extendedProps.room;
            }
            try {
                loader(true);
                const updated = maintenanceData.Id === 0
                    ? await RoomPlannedMaintenance.AddRoomPlannedMaintenances([maintenanceData])
                    : await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenanceData]);
                const updatedItem = Array.isArray(updated) ? updated[0] : updated;

                // Update the event's extendedProps with the new dates so that
                // clicking the event after drag shows the correct dates in the popup
                info.event.setExtendedProp('maintenance', maintenanceData);

                // Use onCreateUpdate to refetch maintenance from API (same as popup save)
                await onCreateUpdate(updatedItem);
                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else {
            info.revert();
        }
    }, [confirm, loader, text, message, getNewProductID, hasMaintenanceConflict]);

    const onEventResize = useCallback(async (info) => {
        isInteractingRef.current = false;
        const confirmMessages = {
            changeDates: {
                header: text('ChangeReservationDates'),
                body: text('WouldYouLikeToChangeThisReservationDates'),
            },
        };

        const ext = safeExtendedProps(info.event);
        if (ext.reservation) {
            let reservation = structuredClone(ext.reservation);

            if (reservation.IsDoNotChangeRoom) {
                if (!UserHasPermission(PermissionsActions.ToggleRoomLock)) {
                    onWarning(text("NoPermission"));
                    info.revert();
                    return;
                }
                if (!(await confirm({
                    header: text('UnlockRoom'),
                    body: text('ConfirmUnlockRoom'),
                }))) {
                    info.revert();
                    return;
                }
                reservation.IsDoNotChangeRoom = false;
            }

            // Calculate the date changes
            const startDelta = info.startDelta;
            const endDelta = info.endDelta;
            const hasStartChange = startDelta && (startDelta.days !== 0 || startDelta.milliseconds !== 0);
            const hasEndChange = endDelta && (endDelta.days !== 0 || endDelta.milliseconds !== 0);

            if (!hasStartChange && !hasEndChange) {
                info.revert();
                return;
            }

            // Confirm the date change with the user
            if (!(await confirm(confirmMessages.changeDates))) {
                info.revert();
                return;
            }

            // Note: For resize operations, we allow extending dates in both directions
            // The onEventDrop handler already validates that drag operations cannot move to past dates
            // But resize/extend is a different use case - extending backward may be needed for business reasons

            // Update reservation dates
            const newCheckInDate = moment(info.event.start);
            const newCheckOutDate = moment(info.event.end);

            reservation.CheckInDate = info.event.start;
            reservation.CheckOutDate = info.event.end;

            // Determine if we're extending in either direction (requires clearing daily items for server rebuild)
            const isExtendingFromStart = hasStartChange && startDelta.days < 0;
            const isExtendingFromEnd = hasEndChange && endDelta.days > 0;
            const isExtending = isExtendingFromStart || isExtendingFromEnd;

            // If extending in either direction, clear daily items so server can rebuild them
            if (isExtending) {
                reservation.ReservationDailyAccommodations = [];
                reservation.ReservationDailyAddons = reservation.ReservationDailyAddons?.filter(
                    (addon) => !addon.ProductId
                ) || [];
            } else {
                // Handle shrinking - filter out items outside the new date range

                // Handle start date change (shrinking from the beginning - moving start date later)
                if (hasStartChange && startDelta.days > 0) {
                    // Filter out accommodations that fall before the new start date
                    reservation.ReservationDailyAccommodations = reservation.ReservationDailyAccommodations?.filter(
                        (accommodation) => moment(accommodation.Date).isSameOrAfter(newCheckInDate, 'day')
                    ) || [];

                    // Filter out addons that fall before the new start date
                    reservation.ReservationDailyAddons = reservation.ReservationDailyAddons?.filter(
                        (addon) => moment(addon.Date).isSameOrAfter(newCheckInDate, 'day')
                    ) || [];
                }

                // Handle end date change (shrinking from the end - moving end date earlier)
                if (hasEndChange && endDelta.days < 0) {
                    reservation.ReservationDailyAccommodations = reservation.ReservationDailyAccommodations?.filter(
                        (accommodation) => moment(accommodation.Date).isBefore(newCheckOutDate, 'day')
                    ) || [];
                    reservation.ReservationDailyAddons = reservation.ReservationDailyAddons?.filter(
                        (addon) => moment(addon.Date).isBefore(newCheckOutDate, 'day')
                    ) || [];
                }
            }

            // Handle time changes (for day view)
            if (hasStartChange && startDelta.milliseconds !== 0) {
                reservation.Eta = moment(info.event.start).format('HH:mm:ss');
            }
            if (hasEndChange && endDelta.milliseconds !== 0) {
                reservation.Edt = moment(info.event.end).format('HH:mm:ss');
            }

            // Check for maintenance conflict before saving
            if (hasMaintenanceConflict(reservation.RoomId, reservation.CheckInDate, reservation.CheckOutDate)) {
                onWarning(message('MaintenanceConflictMessage'));
                info.revert();
                return;
            }

            try {
                // Check for policy changes when dates change
                const newPolicyResult = await checkForPolicyChange(reservation);
                if (newPolicyResult) {
                    reservation.ReservationPolicyId = newPolicyResult.Id;
                    reservation.ReservationPolicy = newPolicyResult;
                }

                loader(true);
                await SaveReservation(reservation);
                if (info.event?.setExtendedProp) {
                    info.event.setExtendedProp('reservation', reservation);
                }

                // Update reservations state to trigger virtualEvents recalculation
                // This ensures villa/complex room blocking reflects new dates
                setReservations(prevReservations =>
                    prevReservations.map(r => r.Id === reservation.Id ? reservation : r)
                );

                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else if (ext.maintenance) {
            // Confirm the change with the user (same pattern as reservation resize)
            if (!(await confirm({
                header: text('ChangeMaintenanceDates'),
                body: text('WouldYouLikeToChangeThisMaintenanceDates'),
            }))) {
                info.revert();
                return;
            }

            const maintenanceObj = Array.isArray(ext.maintenance) && ext.maintenance.length ?
                ext.maintenance[0] : ext.maintenance;
            const maintenanceData = {
                ...maintenanceObj,
                StartDate: toServerDateFormat(info.event.start),
                EndDate: toServerDateFormat(info.event.end),
                Room: info.event.getResources().at(0)?.extendedProps.room,
            };

            try {
                loader(true);
                const updated = await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenanceData]);
                const updatedItem = Array.isArray(updated) ? updated[0] : updated;

                // Update the event's extendedProps with the new dates so that
                // clicking the event after resize shows the correct dates in the popup
                info.event.setExtendedProp('maintenance', maintenanceData);

                // Use onCreateUpdate to refetch maintenance from API (same as popup save)
                await onCreateUpdate(updatedItem);
                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else {
            info.revert();
        }
    }, [confirm, hideHoverPopup, loader, text, message, hasMaintenanceConflict]);

    const checkForPolicyChange = async (reservation) => {
        if (!reservation.Id || !reservation.ReservationPolicyId) {
            return null;
        }

        try {
            loader(true);

            // Get the new product details to check its policy
            const products = await Products.Search({
                Id: reservation.ProductId,
                HotelId: reservation.HotelId,
                FromDate: toServerDateFormat(reservation.CheckInDate),
                ToDate: toServerDateFormat(reservation.CheckOutDate),
                "Pax.Adults": reservation.Adults || 0,
                "Pax.Children": reservation.Children || 0,
                "Pax.Infants": reservation.Infants || 0,
                Includes: ["ReservationPolicy",
                    "ProductPrices",
                    "DailyProductPrices"],
                LanguageId: window.optima?.user?.UserSettings[0]?.LanguageID,
                IncludeReservationPolicyAutoDescription: true,
            });

            loader(false);

            if (products && products.length > 0) {
                const newProduct = products[0];

                // Check if the policy has changed
                if (newProduct.ReservationPolicyId &&
                    newProduct.ReservationPolicyId !== reservation.ReservationPolicyId) {

                    // Show the policy change confirmation popup
                    setPolicyDetails(newProduct.ReservationPolicy);
                    setShowPolicyChangePopup(true);
                    // Wait for user decision
                    return new Promise((resolve, reject) => {
                        window.policyChangeResolve = resolve;
                        window.policyChangeReject = reject;
                    });
                }
            }
            return null;
        } catch (error) {
            onError('Error checking policy change:', error);
            return null;
        }
        finally {
            loader(false)
        }
    };

    const handleApplyNewPolicy = useCallback(() => {
        setShowPolicyChangePopup(false);
        if (window.policyChangeResolve) {
            window.policyChangeResolve(policyDetails);
            window.policyChangeResolve = null;
            window.policyChangeReject = null;
        }
        setPolicyDetails(null);
    }, [policyDetails]);

    const handleKeepCurrentPolicy = useCallback(() => {
        setShowPolicyChangePopup(false);
        setPolicyDetails(null);
        if (window.policyChangeResolve) {
            window.policyChangeResolve(null);
            window.policyChangeResolve = null;
            window.policyChangeReject = null;
        }
    }, []);

    const onCloseInfo = useCallback(() => {
        if (!showShortReservation && !showRoomMaintenance && !showCancelReservation) {
            setShowInfo(false);
            setSelectedEvent(null);
        }
    }, [showShortReservation, showRoomMaintenance, showCancelReservation]);

    const onClosePopup = () => {
        if (showRoomMaintenance) {
            setSelectedEvent(null);
        }
        setShowShortReservation(false)
        setShowRoomMaintenance(false)
        setSelectedTotals(null);
        // Only clear if user cancelled (Id is falsy), otherwise keep for eventDidMount
        if (!newReservationRef.current?.Id) {
            newReservationRef.current = null;
        }
        newMaintenenceRef.current = null;
    }

    const reservationForEdit = useMemo(() => {
        if (!selectedEvent) return null;
        if (!safeExtendedProps(selectedEvent).reservation) return null;
        return {
            ...safeExtendedProps(selectedEvent).reservation,
            ...selectedTotals
        };
    }, [selectedEvent, selectedTotals]);

    const maintenanceForEdit = useMemo(() => {
        if (!selectedEvent) return null;
        if (!safeExtendedProps(selectedEvent).maintenance) return null;
        const maintenance = safeExtendedProps(selectedEvent).maintenance;
        // Handle case where maintenance might be an array
        if (Array.isArray(maintenance)) {
            return maintenance[0] || null;
        }
        return maintenance || null;
    }, [selectedEvent]);

    const reservationHotel = useMemo(() => {
        const reservation = reservationForEdit || newReservationRef.current;
        // Use _hotel from reservation if available (set during createNewReservation)
        if (reservation?._hotel) {
            return reservation._hotel;
        }
        const hotelId = reservation?.HotelId;
        if (!hotelId || !hotels?.length) return null;
        return hotels.find(h => h.Id === hotelId);
    }, [hotels, reservationForEdit, showShortReservation]);

    const updateReservationStatus = useCallback(async reservation => {
        let updated = await UpdateReservationStatus(reservation)
        return updated
    }, [])

    const handleStatusClick = useCallback(async (resource) => {
        const room = resource.extendedProps.room;
        if (!room) return;
        const status = room.HousekeepingStatusId || 1
        const maxStatusId = Math.max(...Object.keys(RoomStatusTypes).filter(key => key !== "0").map(Number));
        const newStatus = (status % maxStatusId) + 1; // Cycle through available statuses
        try {
            await Rooms.SetRoomHousekeepingStatus({
                roomID: room.Id,
                status: newStatus,
            });
            resource.setExtendedProp('room', {
                ...room,
                HousekeepingStatusId: newStatus
            });
        } catch (error) {
            onError(error)
        }
    }, [])

    const renderResourceLabel = useCallback(({ resource }) => {
        if (viewSettings.showHouseKeeping) {
            const status = resource.extendedProps.room?.HousekeepingStatusId || 1
            return <span className={`room-label status-${RoomStatusTypes[status]}`}>
                {resource.title}
                <Icon className='status-icon' link onClick={() => handleStatusClick(resource)} />
            </span>
        } else {
            return <span>
                {resource.title}
            </span>
        }

    }, [handleStatusClick, viewSettings.showHouseKeeping]);

    useEffect(() => {
        if (hotelsWithRooms && viewSettings.showYield) {
            (async () => {
                try {
                    const yieldData = await PMSCalendar.GetYieldData({
                        HotelIds: hotelsWithRooms.map(x => x.Id),
                        FromDate: dateInfo.startStr,
                        ToDate: dateInfo.endStr
                    });
                    setYieldData(yieldData);
                } catch (error) {
                    onError(error);
                }
            })();
        } else {
            setYieldData([]);
        }
    }, [viewSettings.showYield, hotelsWithRooms, dateInfo]);

    useEffect(() => {
        if (hotelsWithRooms && (viewSettings.showHoliday || viewSettings.showComments)) {
            (async () => {
                try {
                    const dateParameters = await PMSCalendar.GetDateParameters({
                        HotelIds: hotelsWithRooms.map(x => x.Id),
                        FromDate: dateInfo.startStr,
                        ToDate: dateInfo.endStr
                    });
                    setDateParameters(dateParameters);
                } catch (error) {
                    onError(error);
                }
            })();
        } else {
            setDateParameters([]);
        }
    }, [viewSettings.showHoliday, viewSettings.showComments, hotelsWithRooms, dateInfo]);

    useEffect(() => {
        if (hotelsWithRooms && viewSettings.showOccupancy) {
            (async () => {
                try {
                    const availability = await PMSCalendar.GetAvailabilityData({
                        HotelIds: hotelsWithRooms.map(x => x.Id),
                        FromDate: dateInfo.startStr,
                        ToDate: dateInfo.endStr
                    });
                    setAvailabilityData(availability);
                } catch (error) {
                    onError(error);
                }
            })();
        } else {
            setAvailabilityData([]);
        }
    }, [viewSettings.showOccupancy, hotelsWithRooms, dateInfo]);

    const renderResourceGroupLane = useCallback(({ groupValue, view }) => {
        if (!hotelsWithRooms) return null;
        const hotel = hotelsWithRooms.find(h => `${h.Name} - ` === groupValue.substring(0, h.Name.length + 3));
        if (!hotel) return null;

        const dates = Array.from(moment.range(view.currentStart, view.currentEnd).by('day', { excludeEnd: true }));
        return <div className='resource-group-lane'>
            {
                dates.map(date => (
                    <ResourceDayCell
                        key={`${hotel.Id}-${date.toISOString()}`}
                        date={date}
                        hotelId={hotel.Id}
                        viewSettings={viewSettings}
                        yieldData={yieldData}
                        dateParameters={dateParameters}
                        availabilityData={availabilityData}
                    />
                ))
            }
        </div>
    }, [hotelsWithRooms, viewSettings, yieldData, dateParameters, availabilityData]);

    const handlePrev = () => {
        let calendarApi = calendarRef.current.getApi();
        calendarApi.prev();
    };

    const handleNext = () => {
        let calendarApi = calendarRef.current.getApi();
        calendarApi.next();
    };

    const handleSetDate = (date) => {
        let calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(date);
    }

    const handleChangeView = (view) => {
        setDefaultView(view);
        setViewSettings(prev => ({ ...prev, defaultView: view }));
        let calendarApi = calendarRef.current.getApi();
        calendarApi.changeView(view);
    };

    const handleToggleSetting = (setting) => {
        setViewSettings((prev) => ({
            ...prev,
            [setting]: !prev[setting]
        }));
    };

    const handleSetReservationIcon = (icon) => {
        setViewSettings((prev) => ({
            ...prev,
            reservationIcon: icon
        }));
    };




    const createNewReservation = useCallback((info, setDefault) => {
        setShowCreateMenu(false)
        if (setDefault) {
            localStorage.setItem(defaultItemKey, "reservation")
        }

        const room = info.resource?.extendedProps.room;
        const hotel = info.resource?.extendedProps.hotel;
        const newReservation = {
            Id: 0,
            ...(info.start && { CheckInDate: info.start }),
            ...(info.end && { CheckOutDate: info.end }),
            RoomId: room?.Id,
            RoomTypeId: room?.TypeId,
            HotelId: hotel?.Id,
            WingId: room?.WingId,
            ReservationPricingTypeId: 1,
            CurrencyId: hotel?.MainCurrencyId || 1,
            TotalAmountProductCurrentBeforeTax: 0,
            TotalAmountProductCurrentAfterTax: 0,
            Quantity: 1,
            _hotel: hotel,
            totals: null
        }
        if (info.view.type === "resourceTimelineDay") {
            newReservation.Eta = moment(info.start).format("HH:mm:ss")
            newReservation.Edt = moment(info.end).format("HH:mm:ss")
        }
        newReservationRef.current = newReservation
        setShowShortReservation(true)
    }, [])

    const createNewMaintenance = useCallback((info, setDefault) => {
        setShowCreateMenu(false)
        if (setDefault) {
            localStorage.setItem(defaultItemKey, "maintenance")
        }

        const room = info.resource?.extendedProps.room;
        const hotel = info.resource?.extendedProps.hotel;
        const newMaintenence = {
            Id: 0,
            StartDate: info.start,
            EndDate: info.end,
            RoomId: room && room.Id,
            RoomTypeId: room && room.TypeId,
            HotelId: hotel && hotel.Id,
            MaintenanceStatusTypeId: 2
        }
        newMaintenenceRef.current = newMaintenence;
        setShowRoomMaintenance(true);
    }, [])

    const createRestrictions = useCallback((info, setDefault) => {
        setShowCreateMenu(false)
        if (setDefault) {
            localStorage.setItem(defaultItemKey, "restriction")
        }
        const room = info.resource?.extendedProps.room;
        const hotel = info.resource?.extendedProps.hotel;
        window.RestrictionsController("open", {
            HotelId: hotel && hotel.Id,
            StartDate: info.start,
            EndDate: info.end,
            RoomTypeIds: [room && room.TypeId]
        })
    }, [])

    const createPriceUpdate = useCallback((info, setDefault) => {
        setShowCreateMenu(false)
        if (setDefault) {
            localStorage.setItem(defaultItemKey, "priceupdate")
        }
        const room = info.resource?.extendedProps.room;
        const hotel = info.resource?.extendedProps.hotel;
        window.PriceUpdateController("open", {
            HotelId: hotel && hotel.Id,
            StartDate: info.start,
            EndDate: info.end,
            RoomTypeIds: [room && room.TypeId]
        })
    }, [])

    // Helper to update a FullCalendar event's common properties
    const updateCalendarEvent = useCallback((event, data, extendedPropName) => {
        if (!event) return;
        
        const { title, color, startDate, endDate, roomId, classNames } = data;
        
        event.setProp('title', title);
        event.setProp('color', color);
        event.setProp('textColor', getContrastYIQ(color || '3788d8'));
        event.setDates(startDate, endDate);
        event.setResources([roomId]);
        event.setExtendedProp(extendedPropName, data.extendedData);
        
        if (classNames) {
            event.setProp('classNames', classNames);
        }
    }, []);

    const onCreateUpdate = async items => {
        // Ensure items is an array
        const itemsArray = Array.isArray(items) ? items : [items];

        // Check if any item is a maintenance record (has MaintenanceStatusTypeId)
        const hasMaintenance = itemsArray.some(r => r.MaintenanceStatusTypeId !== undefined);
        // Check if any item is a reservation (has CheckInDate or CheckOutDate)
        const hasReservations = itemsArray.some(r => r.CheckInDate || r.CheckOutDate);

        const calendar = calendarRef.current?.getApi();
        
        // Update reservations - both state and FullCalendar events
        if (hasReservations) {
            const newReservations = itemsArray.filter(r => r.CheckInDate || r.CheckOutDate);
            // If this callback comes from creating a new reservation (Id: 0),
            // open the reservation sidebar with the saved reservation once it's in the calendar.
            const createdReservation = newReservationRef.current?.Id === 0
                ? newReservations.find(r => r.Id && r.Id !== 0)
                : null;


            setReservations(prevReservations => {
                const updated = [...prevReservations];
                newReservations.forEach(newRes => {
                    const existingIndex = updated.findIndex(r => r.Id === newRes.Id);
                    if (existingIndex >= 0) {
                        updated[existingIndex] = newRes;
                    } else {
                        updated.push(newRes);
                    }
                });
                return updated;
            });

            // Update FullCalendar events directly
            if (calendar) {
                newReservations.forEach(newRes => {
                    const event = calendar.getEventById(newRes.Id);
                    if (event) {
                        const title = viewSettings.showReservationNumber
                            ? `${newRes.Number} ${GetFullName(newRes.Guest)}`
                            : GetFullName(newRes.Guest);
                        updateCalendarEvent(event, {
                            title,
                            color: newRes.Color,
                            startDate: newRes.CheckInDate,
                            endDate: newRes.CheckOutDate,
                            roomId: newRes.RoomId,
                            extendedData: newRes
                        }, 'reservation');
                    }
                });
            }

            if (createdReservation) {
                // Update ref with saved reservation so eventDidMount can find it
                newReservationRef.current = createdReservation;
            }

        }

        // For maintenance changes, refetch from API (same as on load)
        if (hasMaintenance && dateInfo && hotelsWithRooms) {
            try {
                const maintenanceData = await RoomPlannedMaintenance.GetRoomPlannedMaintenances({
                    HotelIds: hotelsWithRooms.map(x => x.Id),
                    FromDate: dateInfo.startStr,
                    ToDate: dateInfo.endStr,
                    Includes: ["Room", "TapechartDisplaySettings"]
                });

                // Update state with new data - this will trigger eventSources to rebuild
                setMaintenance(maintenanceData);
            } catch (error) {
                onError(error);
            }
        }

        // Clear cached totals to force refetch on next selection
        setSelectedTotals(null);

        // Refresh selectedEvent if the selected reservation was updated
        const currentSelectedEvent = selectedEventRef.current;
        if (currentSelectedEvent && calendar) {
            const selectedResId = safeExtendedProps(currentSelectedEvent).reservation?.Id;
            if (selectedResId && itemsArray.some(r => r.Id == selectedResId)) {
                const freshEvent = calendar.getEventById(String(selectedResId));
                if (freshEvent) {
                    setSelectedEvent(freshEvent);
                }
            }
        }
    }

    const handleSelect = useCallback((selectInfo) => {
        // Close multiple popup when selecting on calendar
        setShowMultiplePopup(false);
        setMultiplePopupContext(null);
        setMultiplePopupEvent(null);

        if (selectInfo.view.type !== "resourceTimelineDay") {
            selectInfo = {
                ...selectInfo,
                end: moment(selectInfo.end).subtract(1, 'day').toDate(),
                endStr: moment(selectInfo.end).subtract(1, 'day').toISOString(),
            }
        }
        selectionInfo.current = selectInfo;

        if (selectInfo.jsEvent.shiftKey) {
            localStorage.removeItem(defaultItemKey)
        }

        let defaultItem = localStorage.getItem(defaultItemKey)
        if (defaultItem === null) {
            setShowCreateMenu(true)
        } else if (defaultItem === "reservation") {
            createNewReservation(selectInfo)
        } else if (defaultItem === "maintenance") {
            createNewMaintenance(selectInfo)
        } else {
            setShowCreateMenu(true)
        }
    }, [createNewMaintenance, createNewReservation]);

    const onChangeRestictions = useCallback(() => {
        // Refetch the day data
        if (window.calendar && window.calendar.getEventSourceById) {
            window.calendar.getEventSourceById("daydata")?.refetch()
        }
    }, [])

    const onChangePrices = useCallback(() => {
        // Refetch the price data
        if (window.calendar && window.calendar.getEventSourceById) {
            window.calendar.getEventSourceById("pricedata")?.refetch()
        }
    }, [])

    return <React.Fragment>
        <Sidebar.Pushable className="calendar-container">
            <Sidebar
                visible={showInfo}
                width="wide"
                animation='overlay'
                onHide={onCloseInfo}
                as={Segment}
                direction="right"
                className='reservation-sidebar'
            >
                <ReservationInfo
                    event={selectedEvent}
                    updateReservationStatus={updateReservationStatus}
                    closePanel={() => setShowInfo(false)}
                    openShortReservation={() => setShowShortReservation(true)}
                    onCreateUpdate={onCreateUpdate}
                    refetchReservations={refetchReservations}
                    reservationVisible={showInfo}
                    setSelectedTotals={setSelectedTotals}
                    onCancelReservationOpenChange={setShowCancelReservation}
                />
            </Sidebar>
            <Sidebar.Pusher
                dimmed={showInfo}>
                <div className='calendar'>
                    <CalendarToolbar
                        title={dateInfo?.view.title}
                        defaultView={defaultView}
                        onPrev={handlePrev}
                        onNext={handleNext}
                        onChangeView={handleChangeView}
                        viewSettings={viewSettings}
                        onToggleSetting={handleToggleSetting}
                        onSetReservationIcon={handleSetReservationIcon}
                        setSelectedHotels={setSelectedHotelIds}
                        onSetDate={handleSetDate}
                        hotels={hotelsWithRooms}
                    />
                    <FullCalendar
                        ref={calendarRef}
                        schedulerLicenseKey='GPL-My-Project-Is-Open-Source'
                        plugins={useMemo(() => [resourceTimelinePlugin, interactionPlugin], [])}
                        selectable={canEdit}
                        editable={canEdit}
                        nowIndicator={false}
                        locale={window.user?.UserSettings?.[0]?.Language?.Code}
                        initialView={defaultView}
                        views={views}
                        height="100%"
                        selectMinDistance={10}
                        datesSet={setDateInfo}
                        headerToolbar={false}

                        /* Select */
                        select={handleSelect}

                        /* Events */
                        eventSources={eventSources}
                        eventClick={onEventClick}
                        eventDrop={onEventDrop}
                        eventDragStart={onEventDragStart}
                        eventDragStop={onEventDragStop}
                        eventMouseEnter={onEventEnter}
                        eventMouseLeave={onEventLeave}
                        eventResize={onEventResize}
                        eventResizeStart={onEventResizeStart}
                        eventResizeStop={onEventResizeStop}
                        eventDidMount={(info) => {
                            // Set data attributes for agent/company first letter display
                            const { agentInitial, companyInitial } = safeExtendedProps(info.event);
                            if (agentInitial) {
                                info.el.setAttribute('data-agent-initial', agentInitial);
                            }
                            if (companyInitial) {
                                info.el.setAttribute('data-company-initial', companyInitial);
                            }
                            // Select the newly created reservation when it's mounted in the calendar
                            if (newReservationRef.current?.Id &&
                                String(info.event.id) === String(newReservationRef.current.Id)) {
                                setSelectedEvent(info.event);
                                setShowInfo(true);
                                newReservationRef.current = null;
                            }

                        }}

                        /* Resources */
                        resources={resources}
                        resourceGroupField='group'
                        resourcesInitiallyExpanded={hotelsWithRooms?.length < 2}
                        resourceAreaHeaderContent={text("Rooms")}
                        resourceAreaWidth={320}
                        resourceLabelContent={renderResourceLabel}
                        resourceGroupLaneContent={renderResourceGroupLane}
                        
                        slotLaneClassNames={useCallback(({ date }) => {
                            const locale = window.user?.UserSettings?.[0]?.Language?.Code || 'en';
                            return isWeekendByLocale(locale, date) ? ['fc-weekend'] : [];
                        }, [])}
                        
                        slotLabelClassNames={useCallback(({ date }) => {
                            const locale = window.user?.UserSettings?.[0]?.Language?.Code || 'en';
                            return isWeekendByLocale(locale, date) ? ['fc-weekend'] : [];
                        }, [])}
                    />
                </div>
            </Sidebar.Pusher>
        </Sidebar.Pushable>

        <CreateMenu
            open={showCreateMenu}
            createNewMaintenance={createNewMaintenance}
            createNewReservation={createNewReservation}
            createPriceUpdate={createPriceUpdate}
            createRestrictions={createRestrictions}
            selectionInfo={selectionInfo.current}
            onClose={() => setShowCreateMenu(false)}
        />

        <ValidateModalPermission open={showShortReservation} onClose={onClosePopup} actionId={PermissionsActions.ViewShortReservation}>
            <ShortReservationPopup
                open={showShortReservation}
                reservation={reservationForEdit || newReservationRef.current}
                hotel={reservationHotel}
                onClose={onClosePopup}
                onSave={onCreateUpdate} />
        </ValidateModalPermission>

        <RoomMaintenancePopup
            open={showRoomMaintenance}
            maintenance={maintenanceForEdit || newMaintenenceRef.current}
            onClose={onClosePopup}
            onSave={onCreateUpdate} />
        <RestrictionsPopup controller="RestrictionsController" onSave={onChangeRestictions} />
        <PriceUpdatePopup controller="PriceUpdateController" onSave={onChangePrices} />
        <SelectProductPopup
            open={productSelection.open}
            spec={productSelection.spec}
            onCancel={productSelection.cancel}
            onSelect={productSelection.select}
        />
        <ReservationPolicyUpdateConfirmationPopup
            open={showPolicyChangePopup}
            onApplyNewPolicy={handleApplyNewPolicy}
            onKeepCurrentPolicy={handleKeepCurrentPolicy}
            newPolicyDetails={policyDetails}
        />
        <SchedulerPopup
            open={showPopup}
            context={popupContext}
            reservation={popupReservation}
        />
        <MaintenancePopup
            open={showMaintenancePopup}
            context={maintenancePopupContext}
            maintenance={popupMaintenance}
        />
        <DayUsePopup
            open={showMultiplePopup}
            context={multiplePopupContext}
            event={multiplePopupEvent}
            onClose={() => {
                setShowMultiplePopup(false);
                setMultiplePopupContext(null);
                setMultiplePopupEvent(null);
            }}
            onEventClick={handleMultiplePopupReservationClick}
        />
    </React.Fragment>
};

export default Calendar;